import ApiClient from '@/services/apiClient';
import Input from '@/models/input';
import InputType from '@/models/inputType';
import AntiPassback from '@/models/antipassback';
import Delay from '@/models/delay';
import Alarm from '@/models/alarm';
import ReaderFormat from '@/models/readerFormat';
import MecaConfig from './mecaConfig';
import HaxoFusionConfig from './haxoFusionConfig';
import AlarmInput from './alarmInput';
import GPSCoordinates from './gpscoordinates';
import ReaderLedConfig from './readerLedConfig';
import i18n from '@/i18n';
import { parseSinceDate, formatSinceDate } from '@/utils/sinceDate';

const t = (key, params) => i18n.global.t(key, params);

export default class ReaderConfig {
  position;
  label;
  anti_pass_back;
  reader_formats;
  opening_delay;
  push_button_input_type;
  door_contact_input_type;
  door_blocked_delay;
  vehicle_detection_input;
  alarm_state_input;
  emergency_button_input;
  alarm_control;
  door_label;
  one_door_mode;
  group_counter_active;
  meca_config;
  haxo_fusion_config;   // door of a third-party access control (SPC...), read-only
  remote_opening;
  geolocalization;
  gps_coordinates;
  lpr_source_id;
  led_config;
  buzzer_on_scan;
  reader_state;
  state_since;      // date of the last connection state change

  static constructFromObject(data, obj) {
    if (data) {
      obj = obj || new ReaderConfig();
      obj.position = ApiClient.convertToType(data['position'], 'Integer');
      obj.label = ApiClient.convertToType(data['label'], 'String');
      obj.anti_pass_back = AntiPassback.constructFromObject(data['anti_pass_back']);
      obj.reader_formats = ApiClient.convertToType(data['reader_formats'], [ReaderFormat]);
      obj.opening_delay = Delay.constructFromObject(data['opening_delay']);
      obj.push_button_input_type = InputType.constructFromObject(data['push_button_input_type']);
      obj.door_contact_input_type = InputType.constructFromObject(data['door_contact_input_type']);
      obj.door_blocked_delay = Delay.constructFromObject(data['door_blocked_delay']);
      obj.door_blocked_delay.unit = 'm';
      obj.vehicle_detection_input = Input.constructFromObject(data['vehicle_detection_input']);
      obj.alarm_state_input = AlarmInput.constructFromObject(data['alarm_state_input']);
      obj.emergency_button_input = Input.constructFromObject(data['emergency_button_input']);
      obj.alarm_control = Alarm.constructFromObject(data['alarm_control']);
      obj.door_label = ApiClient.convertToType(data['door_label'], 'String');
      obj.one_door_mode = ApiClient.convertToType(data['one_door_mode'], 'Boolean');
      obj.group_counter_active = ApiClient.convertToType(data['group_counter_active'], 'Boolean') || false;
      obj.meca_config = ApiClient.convertToType(data['meca_config'], MecaConfig);
      obj.haxo_fusion_config = ApiClient.convertToType(data['haxo_fusion_config'], HaxoFusionConfig);
      obj.remote_opening = ApiClient.convertToType(data['remote_opening'], 'Boolean') || false;
      obj.geolocalization =  'geolocalization' in data? data['geolocalization']: Boolean(data['gps_coordinates']);      
      obj.gps_coordinates = ApiClient.convertToType(data['gps_coordinates'], GPSCoordinates)?? new GPSCoordinates();
      obj.lpr_source_id = ApiClient.convertToType(data['lpr_source_id'], 'Integer')?? 0;
      obj.led_config = ReaderLedConfig.constructFromObject(data['led_config']);
      obj.buzzer_on_scan = ApiClient.convertToType(data['buzzer_on_scan'], 'String')?? 'Inactive';
      obj.reader_state = ApiClient.convertToType(data['reader_state'], 'Integer')?? 1;
      obj.state_since = parseSinceDate(data['state_since']);
    }
    return obj;
  }

  toJSON() {
    const json = {
      reader_formats: this.reader_formats,
      position: this.position,
      label: this.label,
      anti_pass_back: this.anti_pass_back,
      door_label: this.door_label,
      one_door_mode: this.one_door_mode,
      group_counter_active: this.group_counter_active,
      remote_opening: this.remote_opening,
    };
    if(this.one_door_mode == false && this.push_button_input_type != "undefined") {
      json.push_button_input_type = this.push_button_input_type;
    }
    if(this.one_door_mode == false && this.door_contact_input_type != "undefined") {
      json.door_contact_input_type = this.door_contact_input_type;
      if(this.door_blocked_delay.value > 0) json.door_blocked_delay = this.door_blocked_delay;
    }
    if(this.opening_delay.value > 0) {
      json.opening_delay = this.opening_delay;
    }
    if(this.vehicle_detection_input.input_number > 0) {
      json.vehicle_detection_input = this.vehicle_detection_input;
    }
    if(this.alarm_state_input.mode > 0) {
      json.alarm_state_input = this.alarm_state_input;
    }
    if(this.emergency_button_input.input_number > 0) {
      json.emergency_button_input = this.emergency_button_input;
    }
    if(this.alarm_control.enable) {
      json.alarm_control = this.alarm_control;
    }
    if(this.meca_config) {
      json.meca_config = this.meca_config;
    }
    if(this.geolocalization) {
      json.gps_coordinates = this.gps_coordinates;
    }
    if(this.lpr_source_id > 0) {
      json.lpr_source_id = this.lpr_source_id;
    }
    if(this.is_rs485) {
      json.led_config = this.led_config;
      if(this.buzzer_on_scan != 'Inactive') {
        json.buzzer_on_scan = this.buzzer_on_scan;
      }
    }
    return json;
  }

  toString() {
    return this.label;
  }

  clone() {
    return ReaderConfig.constructFromObject(this);
  }

  update(updatedReader) {
    ReaderConfig.constructFromObject(updatedReader, this);
  }

  get getOpeningDelay() {
    return this.opening_delay.value == 0? 0 : 1;
  }

  set getOpeningDelay(value) {
    this.opening_delay.value = Number(value);
  }

  get is_meca_reader() {
    return this.meca_config != undefined;
  }

  get is_uz_reader() {
    return this.is_meca_reader && this.meca_config.provider === 'UZ';
  }

  get is_aperio_reader() {
    return this.is_meca_reader && this.meca_config.provider === 'Aperio';
  }

  // Door of a third-party access control mirrored by the LINK (HAXO Fusion): only its labels are configurable
  get is_fusion_reader() {
    return this.haxo_fusion_config != undefined;
  }

  get is_spc_reader() {
    return this.is_fusion_reader && this.haxo_fusion_config.is_spc;
  }

  // Type shown in the components trees: the provider of a mechatronic reader or of an SPC door, as in the app
  get typeLabel() {
    if (this.is_spc_reader) return t('common.readerSpc');
    return this.is_meca_reader ? this.meca_config.provider : t('common.readerBadges');
  }

  get is_rs485() {
    return this.reader_formats.some(format => format.is_rs485);
  }

  // As in the app, RS485 and mechatronic readers show their connection state, the others are always active
  get hasConnectionState() {
    return (Array.isArray(this.reader_formats) && this.is_rs485) || this.is_meca_reader;
  }

  get stateLabel() {
    if(!this.hasConnectionState) return t('common.active');
    switch(this.reader_state) {
      case 0: return t('common.disconnected');
      case 1: return t('common.connected');
      default: return t('common.connecting');
    }
  }

  get isStateActive() {
    return !this.hasConnectionState || this.reader_state === 1;
  }

  // "Connected since ..." / "Disconnected since ..." as in the app, null when unknown
  get stateSinceText() {
    if(!this.state_since || !this.hasConnectionState) return null;
    const date = formatSinceDate(this.state_since);
    switch(this.reader_state) {
      case 0: return t('common.disconnectedSince', { date });
      case 1: return t('common.connectedSince', { date });
      default: return null;
    }
  }

  key(hubId) {
    return `${hubId}_${this.position}`;
  }

  path(facilityId, hubId) {
    return {path: '/component-edit-reader', params: { facilityId: facilityId, hubId: hubId, position: this.position }};
  }
}
